<template>
  <div>
    <div
      v-if="loading"
      class="text-center loading"
    >
      <span class="icon icon-spinner"></span>
    </div>

    <!-- Search -->
    <div class="filter-bar">
      <form
        class="tb-form-group"
        @submit.prevent="keywordSearch()"
      >
        <div class="shoppable-search">
          <i class="icon icon-magnifier"></i>
          <input
            v-model="keyword"
            type="text"
            class="tb-input"
            placeholder="Search"
            :disabled="loading"
            ref="searchInput"
            data-entity="producer-shoppable-searchInput"
          >
          <button
            v-if="keyword"
            class="shoppable-search__clear"
            type="button"
            data-entity="producer-shoppable-clearSearch"
            @click="clearSearch"
          >
            <span class="fa fa-close"></span>
          </button>
        </div>

        <button
          type="submit"
          :disabled="loading || !keyword"
          class="tb-btn tb-btn-contained medium-em margin-left-md"
          data-entity="producer-shoppable-searchButton"
        >
          Search
        </button>
      </form>
      <!-- Chip filters -->
      <div class="flex">
        <div class="shoppable-filters">
          <p class="shoppable-filters__title">Gender</p>

          <div class="shoppable-filters__chips">
            <label
              v-for="(v, k) in genders"
              class="shoppable-chip"
              :key="k"
            >
              <span
                :class="{
                  'shoppable-chip__content': true,
                  'shoppable-chip__content--checked': chipFilters.includes(v),
                }"
              >
                {{ k }}
              </span>
              <input
                v-model="chipFilters"
                :value="v"
                type="checkbox"
                class="shoppable-chip__input"
                :data-test="`producer-shoppable-${k}Filter`"
              >
            </label>
          </div>
        </div>
        <div class="shoppable-filters">
          <p class="shoppable-filters__title">Category</p>

          <div class="shoppable-filters__chips">
            <label
              v-for="(v, k) in keywordFilters"
              class="shoppable-chip"
              :key="k"
            >
              <span
                :class="{
                  'shoppable-chip__content': true,
                  'shoppable-chip__content--checked': chipFilters.includes(v),
                }"
              >
                {{ k }}
              </span>
              <input
                v-model="chipFilters"
                :value="v"
                type="checkbox"
                class="shoppable-chip__input"
                :data-test="`producer-shoppable-${k}Filter`"
              >
            </label>
          </div>
        </div>
        <div class="shoppable-filters flex-grow">
          <p class="shoppable-filters__title">Price</p>

          <span class="text-xxs text-os-medium">${{ priceLow }}</span>
          <div class="tb-range-slider tb-range-slider--round margin-left-xxs margin-right-xxs">
            <input
              class="tb-range-slider__input"
              type="range"
              :min="0"
              :step="25"
              :max="maxPrice"
              v-model.number="priceLow"
              data-entity="producer-shoppable-lowPriceFilter"
            />

            <input
              class="tb-range-slider__input"
              type="range"
              :min="0"
              :step="25"
              :max="maxPrice"
              v-model.number="priceHigh"
              data-entity="producer-shoppable-highPriceFilter"
            />
          </div>
          <span class="text-xxs text-os-medium">${{ priceHigh }}{{ priceHigh === maxPrice ? '+' : '' }}</span>
        </div>
      </div>
    </div>

    <!-- Results -->
    <div v-show="!loading" class="margin-top-xl shoppable-results">
      <template v-if="results.length">
        <p class="text-os-low text-xs text-right">{{ this.total }} results found</p>
        <div
          v-for="r in results"
          :key="r.sourceId"
          class="shoppable-result"
          :class="{
            'shoppable-result': true,
          }"
        >
          <img
            :src="r.imageUrl"
            class="shoppable-result__image"
            alt=""
          >
          <div class="shoppable-result__info">
            <p class="shoppable-result__details">{{ r.price }} {{ r.currency }} <span>(ID: {{ r.sourceId }})</span> </p>
            <p
              class="shoppable-result__name ellipsis"
            >
              <a
                :href="r.sourceUrl"
                target="_blank"
                class="shoppable-result__link text-os-medium"
              >
                <span class="tgb-sr-only">Link to product page</span>
                <span class="fa fa-external-link"></span>
                <span>{{ r.name }}</span>
              </a>
            </p>
          </div>
          <button
            class="tb-btn tb-btn-contained medium-em"
            :class="{
              'high-em tb-btn-contained--override-disabled': savedItemLookup[r.sourceId],
            }"
            :disabled="savedItemLookup[r.sourceId]"
            :data-test="`producer-shoppable-selectProduct-${r.sourceId}`"
            @click="chooseItem(r)"
          >
            {{ savedItemLookup[r.sourceId] ? 'Saved' : 'Select' }}
          </button>
        </div>
        <div
          v-if="this.total > 10 && this.page < this.pages"
          class="shoppable-load-more"
        >
          <button
            type="submit"
            :disabled="loading"
            class="tb-btn tb-btn-contained medium-em shoppable-load-more__button"
            data-entity="producer-shoppable-loadMore"
            @click="loadMore"
          >
            Load More
          </button>
        </div>
      </template>

      <div
        v-else-if="noResults"
        class="text-os-low text-xs text-center"
      >
        <p class="text-bold">
          No products found.
        </p>
        <p>
          Check your spelling or try filtering for better results.
        </p>
      </div>
      <p
        v-else
        class="text-os-low text-xs text-center"
      >Search a product to get started</p>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash-es';

import CatalogMixin from '@/js/mixins/shoppable-catalog';

export default {
  name: 'FanaticsCatalog',
  mixins: [CatalogMixin],
  data() {
    return {
      chipFilters: [],
      genders: {
        Men: 'gender:Male',
        Women: 'gender:Female',
      },
      keywordFilters: {
        Jersey: 'keyword:Jersey',
        Hats: 'keyword:Hat',
        Sweatshirts: 'keyword:Sweatshirt',
        'T-Shirts': 'keyword:T-Shirt',
      },
      priceLow: 0,
      priceHigh: 150,
      maxPrice: 150,
    };
  },

  computed: {
    priceRange() {
      if (this.priceHigh === this.maxPrice && this.priceLow === 0) {
        return '';
      } 
      
      if (this.priceHigh === this.maxPrice) {
        return `price:${this.priceLow}-`;
      }

      return `price:${this.priceLow}-${this.priceHigh}`;
    },
    allFilters() {
      return [
        ...this.chipFilters,
        this.priceRange,
      ];
    },
    queryParams() {
      return {
        filters: this.allFilters,
      };
    },
  },

  created() {
    this.store = 'fanatics'; // set data for mixin
  },
  mounted() {
    this.$refs.searchInput.focus();
  },

  watch: {
    allFilters: debounce(function() {
      this.keywordSearch();
    }, 500),
    priceLow(v) {
      if (v > this.priceHigh) {
        this.priceHigh = v;
      }
    },
    priceHigh(v) {
      if (v < this.priceLow) {
        this.priceLow = v;
      }
    },
  },
};
</script>

<style lang="scss">
.shoppable-search {
  flex: 1;
  position: relative;

  .tb-input { //overrides
    padding-right: 30px;
  }

  &__clear {
    border: none;
    background-color: transparent;
    position: absolute;
    right: 0;
    top: 50%;
    transform: translate(-5px, -50%);
  }
}

.shoppable-filters {
  display: flex;
  align-items: center;

  & + & {
    margin-left: 12px;
  }
  
  &__title {
    color: variables.$color-on-surface-medium;
    margin-bottom: 0;
    margin-right: 8px;
    font-size: variables.$text-xs;
  }
  
  &__chips {
    display: flex;
    gap: 8px;
  }
}

.shoppable-chip {
  cursor: pointer;
  margin: 0;
  font-size: variables.$text-xs;

  &__content {
    display: block;
    padding: 6px;
    border: 1px solid #4D6870;
    border-radius: 5px;
    color: variables.$primary-blue;

    &--checked {
      border-color: #23CDF4;
      background-color: #253C42;
    }
  }

  &__input {
    position: absolute;
    opacity: 0;
  }
}

.shoppable-results {
  max-height: 50vh;
  overflow-y: auto;
}

.shoppable-result {
  display: flex;
  align-items: center;
  padding: 8px;
  border: 1px solid transparent;
  border-radius: 4px;
  background-color: variables.$color-03dp;

  & + & {
    margin-top: 8px;
  }

  &__image {
    height: 56px;
    width: 56px;
    object-fit: contain;
    margin: 8px 0;
  }

  &__info {
    padding: 0 12px;
    min-width: 0;
    flex: 1 0 0;
    margin-right: 8px;
  }

  &__details {
    span {
      display: inline-block;
      margin-left: 8px;
      color: variables.$color-on-surface-low;
    }
  }

  &__name {
    flex: 1 0 auto;
  }

  &__link {
    margin-right: 4px;
  }
}

.shoppable-load-more {
  width: 100%;
  display: flex;
  margin-top: 8px;

  &__button {
    margin: 0 auto;
  }
}
</style>
